import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
// Updated: Added Total Group Savings feature
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import SummaryCard from './components/SummaryCard';
import ContributionHistoryTable from './components/ContributionHistoryTable';
import NotificationPanel from './components/NotificationPanel';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import {
  ContributionRecord,
  NotificationItem,
} from './types';

const MemberDashboard = () => {
  const { user, userProfile, effectiveRole } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [totalContributions, setTotalContributions] = useState(0); // member's own total
  const [totalGroupSavings, setTotalGroupSavings] = useState(0); // total group savings
  const [contributionHistory, setContributionHistory] = useState<ContributionRecord[]>([]); // full group history
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [currentGroupId, setCurrentGroupId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'contributions'>('overview');

  // If this user is assigned as a group admin/clerk (group_members.role),
  // they should see admin UI, not member dashboard.
  const hasGroupAdminRole = Boolean(
    userProfile?.group_roles?.some((r) => r === 'admin' || r === 'clerk')
  );

  // Listen for tab changes from sidebar
  useEffect(() => {
    const handleTabChange = (event: CustomEvent) => {
      const tab = event.detail.tab;
      if (tab === 'overview' || tab === 'contributions') {
        setActiveTab(tab);
      }
    };

    window.addEventListener('dashboard-tab-change', handleTabChange as EventListener);
    return () => {
      window.removeEventListener('dashboard-tab-change', handleTabChange as EventListener);
    };
  }, []);

  // If this user is a group-scoped admin (group_members.role='admin'/'clerk'), show admin dashboard instead.
  useEffect(() => {
    if (!userProfile) return;
    if (effectiveRole === 'admin' && userProfile.role === 'member') {
      navigate('/admin-dashboard', { replace: true });
    }
  }, [effectiveRole, navigate, userProfile]);

  // Fetch member data
  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      try {
        setLoading(true);

        // Get user's group
        const { data: memberGroup, error: memberError } = await supabase
          .from('group_members')
          .select('group_id')
          .eq('user_id', user.id)
          .limit(1)
          .single();

        if (memberError || !memberGroup) {
          console.error('No group found for member');
          setLoading(false);
          return;
        }

        const groupId = memberGroup.group_id;
        setCurrentGroupId(groupId);

        // Fetch all contributions for the group (members can see all transactions, read-only)
        const { data: contributions, error: contribError } = await supabase
          .from('contributions')
          .select(`
            id,
            member_id,
            amount,
            contribution_date,
            notes,
            status,
            recorded_by,
            created_at,
            recorder:user_profiles!recorded_by (
              id,
              full_name
            ),
            member:user_profiles!member_id (
              id,
              full_name
            )
          `)
          .eq('group_id', groupId)
          .order('contribution_date', { ascending: false });

        if (contribError) throw contribError;

        // Map to contribution records (full group history)
        const history: ContributionRecord[] = (contributions || []).map((contrib: any) => ({
          id: contrib.id,
          date: new Date(contrib.contribution_date),
          amount: parseFloat(contrib.amount || 0),
          recordedBy: contrib.recorder?.full_name || 'Unknown',
          recordingDate: contrib.created_at ? new Date(contrib.created_at) : undefined,
          verificationStatus:
            contrib.status === 'approved'
              ? 'verified'
              : contrib.status === 'pending_approval'
                ? 'pending'
                : 'verified',
          notes: contrib.notes || undefined,
          memberName: contrib.member?.full_name || 'Unknown member',
          memberId: contrib.member_id,
          isCurrentUser: contrib.member_id === user.id,
        }));

        setContributionHistory(history);

        // Calculate member's own total contributions (balance)
        const memberTotal = history
          .filter((h) => h.isCurrentUser)
          .reduce((sum, h) => sum + h.amount, 0);
        setTotalContributions(memberTotal);

        // Calculate total group savings (all contributions)
        const groupTotal = history
          .reduce((sum, h) => sum + h.amount, 0);
        setTotalGroupSavings(groupTotal);

        // Fetch notifications only for the current member's contributions
        const { data: memberContributions, error: memberContribError } = await supabase
          .from('contributions')
          .select(`
            id,
            amount,
            contribution_date,
            status,
            member:user_profiles!member_id (
              id,
              full_name
            )
          `)
          .eq('group_id', groupId)
          .eq('member_id', user.id)
          .order('contribution_date', { ascending: false })
          .limit(20);

        if (!memberContribError && memberContributions) {
          const notificationItems: NotificationItem[] = memberContributions.map((contrib: any) => ({
            id: contrib.id,
            type: contrib.status === 'pending_approval' ? 'confirmation' : 'verification',
            message: `You contributed ${parseFloat(contrib.amount || 0).toFixed(2)} VT on ${new Date(contrib.contribution_date).toLocaleDateString()}`,
            timestamp: new Date(contrib.contribution_date),
            actionRequired: contrib.status === 'pending_approval'
          }));

          setNotifications(notificationItems);
        }

      } catch (error) {
        console.error('Error fetching member data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleRequestLoan = () => {
    navigate('/loan-request');
  };

  const handleViewDetails = (id: string) => {
    console.log('View contribution details:', id);
  };

  const handleNotificationAction = (id: string) => {
    console.log('Handle notification action:', id);
  };

  const handleNotificationDismiss = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // IMPORTANT: Avoid returning early before hooks (causes React invariant 300 on refresh).
  // This redirect must be AFTER hooks.
  if (user && userProfile?.role === 'member' && hasGroupAdminRole) {
    return <Navigate to="/admin-dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      <Header
        onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMobileMenuOpen={isMobileMenuOpen}
      />

      <div className="flex">
        <div className="hidden lg:block">
          <Sidebar
            isCollapsed={isSidebarCollapsed}
            userRole="member"
            activeDashboardTab={activeTab}
          />
        </div>

        <main
          className={`
            flex-1 transition-layout
            ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}
          `}
        >
          <div className="p-6 lg:p-8">
            <div className="mb-6">
              <BreadcrumbTrail />
            </div>

            <div className="mb-6 sm:mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="space-y-1">
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground">
                    Welcome back, {userProfile?.full_name || 'Member'}! 👋
                  </h1>
                  <p className="text-sm sm:text-base text-muted-foreground">
                    View your contribution totals and history
                  </p>
                </div>
                <div className="flex gap-2 sm:gap-3">
                  <Button
                    onClick={handleRequestLoan}
                    variant="default"
                    iconName="CreditCard"
                    iconPosition="left"
                    className="flex-1 sm:flex-none shadow-md hover:shadow-lg transition-shadow"
                  >
                    <span className="hidden sm:inline">Request Loan</span>
                    <span className="sm:hidden">Loan</span>
                  </Button>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="mb-6 border-b border-border">
              <nav className="flex space-x-8">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'overview'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                    }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab('contributions')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'contributions'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                    }`}
                >
                  Contributions
                </button>
              </nav>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12 sm:py-16">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                  <div className="text-muted-foreground font-medium">Loading your dashboard...</div>
                </div>
              </div>
            ) : (
              <>
                {activeTab === 'overview' && (
                  <>
                    {(() => {
                      const memberHistory = contributionHistory.filter((c) => c.isCurrentUser);
                      const memberCount = memberHistory.length;
                      const averageAmount =
                        memberCount > 0 ? totalContributions / memberCount : 0;

                      return (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
                          <SummaryCard
                            title="Total Contributions"
                            value={`${totalContributions.toFixed(2)} VT`}
                            iconName="DollarSign"
                            iconColor="var(--color-success)"
                            trend={{ value: 0, isPositive: true }}
                            description="All time total"
                          />
                          <SummaryCard
                            title="Number of Contributions"
                            value={memberCount.toString()}
                            iconName="FileText"
                            iconColor="var(--color-primary)"
                            trend={{ value: 0, isPositive: true }}
                            description="Number of contributions"
                          />
                          <SummaryCard
                            title="Average Amount"
                            value={`${averageAmount.toFixed(2)} VT`}
                            iconName="TrendingUp"
                            iconColor="var(--color-accent)"
                            trend={{ value: 0, isPositive: true }}
                            description="Per contribution"
                          />
                          <SummaryCard
                            title="Total Group Savings"
                            value={`${totalGroupSavings.toFixed(2)} VT`}
                            iconName="Wallet"
                            iconColor="var(--color-warning)"
                            trend={{ value: 0, isPositive: true }}
                            description="Group total savings"
                          />
                        </div>
                      );
                    })()}

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
                      <div className="lg:col-span-2 order-2 lg:order-1">
                        <div className="bg-card border border-border rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
                          <h2 className="text-base sm:text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                            <span className="text-xl sm:text-2xl">📋</span>
                            <span>Recent Group Contributions</span>
                          </h2>
                          <ContributionHistoryTable
                            contributions={contributionHistory.slice(0, 5)}
                            onViewDetails={handleViewDetails}
                          />
                        </div>
                      </div>
                      <div className="order-1 lg:order-2">
                        <NotificationPanel
                          notifications={notifications}
                          onActionClick={handleNotificationAction}
                          onDismiss={handleNotificationDismiss}
                        />
                      </div>
                    </div>
                  </>
                )}

                {activeTab === 'contributions' && (
                  <div className="bg-card border border-border rounded-xl p-4 sm:p-6 shadow-lg">
                    <h2 className="text-base sm:text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                      <span className="text-xl sm:text-2xl">📊</span>
                      <span>Contribution History</span>
                    </h2>
                    <ContributionHistoryTable
                      contributions={contributionHistory}
                      onViewDetails={handleViewDetails}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      <button
        onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        className="hidden lg:flex fixed bottom-6 left-6 w-12 h-12 bg-primary text-primary-foreground rounded-full items-center justify-center shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 z-50"
        aria-label="Toggle sidebar"
      >
        <span className="text-lg font-bold">
          {isSidebarCollapsed ? '→' : '←'}
        </span>
      </button>
    </div>
  );
};

export default MemberDashboard;